import { Pressable, ScrollView, Text, View } from 'react-native'
import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { BUYER_CONFIRM_BUY_AMOUNT, BUYER_DISAGREE_BUY_AMOUNT } from '@/constants/graphql/mutations'
import type { BuyOrder } from '@/types/common'
import { cn } from '@/utils/cn'
import { delay } from '@/utils/delay'
import { Banner, Button, ModalFooter, useModal, useToast } from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import { Close } from '@/components/icon'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import { ChildOrderDetailSection } from '@/components/orders/child/DetailSection'
import { ChildOrderPaymentSection } from '@/components/orders/child/PaymentSection'
import { ImageViewerSection } from '@/components/orders/ImageViewerSection'

import { RevisingOrderSection } from '../RevisingOrderSection'

export function ChildOrderRevisingScreen({
  data,
  type,
  parentInternalKey,
  refetch,
  requestAppeal
}: {
  data: BuyOrder
  type: 'buy' | 'sell'
  parentInternalKey?: string
  refetch: () => void
  requestAppeal: () => void
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const loading = useLoadingIndicator()
  const { bottom } = useSafeAreaInsets()
  const { openModal, closeModal } = useModal()

  const [confirmBuyAmount] = useMutation(BUYER_CONFIRM_BUY_AMOUNT, {
    onCompleted: async () => {
      await delay(500)
      refetch()
    },
    onError: (error) => {
      refetch()
      loading.stop()
      console.log('error:', error)
      toast.show({ variant: 'error', description: t('auth:toast.error.invalidOperationHint') })
    }
  })
  const [disagreeBuyAmount] = useMutation(BUYER_DISAGREE_BUY_AMOUNT, {
    onCompleted: async () => {
      await delay(500)
      refetch()
    },
    onError: (error) => {
      refetch()
      loading.stop()
      console.log('error:', error)
      toast.show({ variant: 'error', description: t('auth:toast.error.invalidOperationHint') })
    }
  })

  function handleAgreeAmount() {
    closeModal()
    // console.log({ buyOrderId: data.id })
    loading.start()
    confirmBuyAmount({
      variables: {
        buyOrderId: data.id
      }
    })
  }

  function handleDisAgreeAmount() {
    closeModal()
    // console.log({ buyOrderId: data.id })
    loading.start()
    disagreeBuyAmount({
      variables: {
        buyOrderId: data.id
      }
    })
  }

  function handleOpenModal() {
    openModal({
      children: (
        <>
          <View className={'gap-1 p-4'}>
            <Text className={'text-base font-medium text-text-primary'}>{t('orders:respondToOrderChange')}</Text>
            <Text className={'text-sm text-text-secondary'}>{t('orders:agreeToOrderChange')}</Text>
            <Pressable className={'absolute right-2 top-2 size-10 items-center justify-center'} onPress={closeModal}>
              <Close className={'text-text-tertiary'} />
            </Pressable>
          </View>
          <RevisingOrderSection
            hasHeader={false}
            dealAmount={data.dealAmount}
            originDealAmount={data.originDealAmount}
            basePrice={data.basePrice}
            changeAmountProof={null}
          />
          <ModalFooter>
            <Button className={'flex-1'} variant={'outline'} size={'md'} onPress={handleDisAgreeAmount}>
              {t('orders:disagree')}
            </Button>
            <Button className={'flex-1'} size={'md'} onPress={handleAgreeAmount}>
              {t('orders:agree')}
            </Button>
          </ModalFooter>
        </>
      ),
      onClose: closeModal
    })
  }
  return (
    <View className={'flex-1 bg-background-secondary'}>
      <ScrollView className={'flex-1'} contentContainerStyle={{ paddingBottom: bottom + 72 }}>
        <View
          className={cn(
            'border-b border-border-10 bg-background-primary px-4 pt-2',
            type === 'buy' ? 'gap-3 pb-[15px]' : 'gap-2 pb-[11px]'
          )}
        >
          <Text className={'text-2xl font-semibold text-text-primary'}>{t('orders:revisingOrder')}</Text>
          {type === 'buy' ? (
            <Banner mood={'warning'}>{t('orders:sellerRequestedOrderChange')}</Banner>
          ) : (
            <Text className={'text-sm text-text-secondary'}>{t('orders:orderChangeSubmitted')}</Text>
          )}
        </View>
        <RevisingOrderSection
          dealAmount={data.dealAmount}
          originDealAmount={data.originDealAmount}
          basePrice={data.basePrice}
          changeAmountProof={data.changeAmountProofBucketPath}
        />
        <ChildOrderPaymentSection data={data} type={type} />
        <ImageViewerSection images={data.paymentProofUrl} />
        <ChildOrderDetailSection data={data} type={type} parentInternalKey={parentInternalKey} />
      </ScrollView>
      {type === 'buy' && (
        <BottomActionPanel>
          <Button
            size={'md'}
            intent={'primary'}
            variant={'solid'}
            className={'flex-1'}
            onPress={handleOpenModal}
            disabled={loading.isLoading}
          >
            {t('orders:respondToOrderChange')}
          </Button>
        </BottomActionPanel>
      )}
      {type === 'sell' && (
        <BottomActionPanel>
          <Button
            size={'md'}
            intent={'primary'}
            variant={'outline'}
            className={'flex-1'}
            onPress={requestAppeal}
            disabled={loading.isLoading}
          >
            {t('orders:startAppeal')}
          </Button>
        </BottomActionPanel>
      )}
    </View>
  )
}
